import {
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import AttachFileSharpIcon from "@mui/icons-material/AttachFileSharp";
import { InternalProjectDto, TraineeChoiceDto } from "../../../../types/traineesTypes";
import { DepartmentDetailsDto, MentorDto } from "../../../../types/membersTypes";
import LoadingTable from "../../../general/LoadingTable";

interface TraineePreferencesTableProps {
  traineePreferences: TraineeChoiceDto[];
  changeRessortChoice: (memberId: number, newValue: string) => void;
  filteredDepartments: DepartmentDetailsDto[];
  changeMentorChoice: (memberId: number, newValue: string) => void;
  mentorsOfGeneration: MentorDto[];
  changeInternalProjectChoice: (memberId: number, newValue: string) => void;
  internalProjectsOfGeneration: InternalProjectDto[];
  openMotivationalTexts: (trainee: TraineeChoiceDto) => void;
}

/**
 * Component which renders the table of trainee preferences in the AssignTrainees page
 * @param traineePreferences - The trainee preferences to be rendered in the table
 * @param changeRessortChoice - Function to change the ressort choice of a trainee
 * @param filteredDepartments - The departments of the company to be rendered in the select field for the "Andere" option of the ressort choice
 * @param changeMentorChoice - Function to change the mentor choice of a trainee
 * @param mentorsOfGeneration - The mentors of the generation to be rendered in the select field for the "Andere" option of the mentor choice
 * @param changeInternalProjectChoice - Function to change the internal project choice of a trainee
 * @param internalProjectsOfGeneration - The internal projects of the generation to be rendered in the select field for the "Andere" option of the internal project choice
 * @param openMotivationalTexts - Function to open the modal with the motivational texts of a trainee
 * @returns The table of trainee preferences
 */
const TraineePreferencesTable = ({
  traineePreferences,
  changeRessortChoice,
  filteredDepartments,
  changeMentorChoice,
  mentorsOfGeneration,
  changeInternalProjectChoice,
  internalProjectsOfGeneration,
  openMotivationalTexts,
}: TraineePreferencesTableProps) => {
  const theme = useTheme();
  /**
   * Function which proivdes the styles of the AssignTraineePreferences
   */
  const styles = {
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    tableCell: {
      padding: "16px 8px 16px 8px",
    },
  };
  return traineePreferences.length === 0 ? (
    <LoadingTable />
  ) : (
    <TableContainer component={Paper} sx={{ maxHeight: (window.screen.height - 75) * 0.8 }}>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            <TableCell sx={styles.tableHeadCell}>
              <Typography>Trainee</Typography>
            </TableCell>
            <TableCell sx={styles.tableHeadCell}>
              <Typography>Ressort</Typography>
            </TableCell>
            <TableCell sx={styles.tableHeadCell}>
              <Typography>Mentor</Typography>
            </TableCell>
            <TableCell sx={styles.tableHeadCell}>
              <Typography>Internes Projekt</Typography>
            </TableCell>
            <TableCell sx={styles.tableHeadCell}>
              <Typography></Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {traineePreferences.map((trainee, index) => (
            <TableRow hover key={index}>
              <TableCell component="th" scope="row" sx={styles.tableCell}>
                <Typography color="secondary">
                  <Link
                    to={`/gesamtuebersicht/${trainee.memberId}`}
                    style={{ textDecoration: "none", boxShadow: "none", color: "black" }}
                  >{`${trainee.firstname} ${trainee.lastname}`}</Link>
                </Typography>
              </TableCell>
              <TableCell sx={styles.tableCell}>
                <RadioGroup row aria-labelledby="demo-row-radio-buttons-group-label" name="row-radio-buttons-group">
                  <FormControlLabel
                    disabled={trainee.departmentChoice1 === null}
                    checked={
                      trainee.departmentChoice1 === trainee.departmentChoice && trainee.departmentChoice1 !== null
                    }
                    value={trainee.departmentChoice1 === null ? "" : trainee.departmentChoice1}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeRessortChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={trainee.departmentChoice1ShortName === null ? "n/a" : trainee.departmentChoice1ShortName}
                  />
                  <FormControlLabel
                    disabled={trainee.departmentChoice2 === null}
                    checked={
                      trainee.departmentChoice2 === trainee.departmentChoice && trainee.departmentChoice2 !== null
                    }
                    value={trainee.departmentChoice2 === null ? "" : trainee.departmentChoice2}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeRessortChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={trainee.departmentChoice2ShortName === null ? "n/a" : trainee.departmentChoice2ShortName}
                  />
                  <FormControlLabel
                    disabled={trainee.departmentChoice3 === null}
                    checked={
                      trainee.departmentChoice3 === trainee.departmentChoice && trainee.departmentChoice3 !== null
                    }
                    value={trainee.departmentChoice3 === null ? "" : trainee.departmentChoice3}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeRessortChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={trainee.departmentChoice3ShortName === null ? "n/a" : trainee.departmentChoice3ShortName}
                  />
                  <FormControlLabel
                    checked={
                      trainee.departmentChoice !== null &&
                      trainee.departmentChoice1 !== trainee.departmentChoice &&
                      trainee.departmentChoice2 !== trainee.departmentChoice &&
                      trainee.departmentChoice3 !== trainee.departmentChoice
                    }
                    control={<Radio onClick={() => changeRessortChoice(trainee.memberId, "")} />}
                    label={
                      <TextField
                        select
                        sx={{ minWidth: "80px" }}
                        label="Andere"
                        value={
                          trainee.departmentChoice !== null &&
                          trainee.departmentChoice1 !== trainee.departmentChoice &&
                          trainee.departmentChoice2 !== trainee.departmentChoice &&
                          trainee.departmentChoice3 !== trainee.departmentChoice
                            ? trainee.departmentChoice
                            : ""
                        }
                        onChange={(event) => changeRessortChoice(trainee.memberId, event.target.value.toString())}
                      >
                        {filteredDepartments.map((department) => {
                          return (
                            <MenuItem key={department.departmentId} value={department.departmentId}>
                              {department.shortName}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    }
                  />
                </RadioGroup>
              </TableCell>
              <TableCell sx={styles.tableCell}>
                <RadioGroup row aria-labelledby="demo-row-radio-buttons-group-label" name="row-radio-buttons-group">
                  <FormControlLabel
                    disabled={trainee.mentorChoice1 === null}
                    checked={trainee.mentorChoice1 === trainee.mentorChoice && trainee.mentorChoice1 !== null}
                    value={trainee.mentorChoice1 === null ? "" : trainee.mentorChoice1}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeMentorChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={trainee.mentorChoice1Name === null ? "n/a" : trainee.mentorChoice1Name}
                  />
                  <FormControlLabel
                    disabled={trainee.mentorChoice2 === null}
                    checked={trainee.mentorChoice2 === trainee.mentorChoice && trainee.mentorChoice2 !== null}
                    value={trainee.mentorChoice2 === null ? "" : trainee.mentorChoice2}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeMentorChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={trainee.mentorChoice2Name === null ? "n/a" : trainee.mentorChoice2Name}
                  />
                  <FormControlLabel
                    disabled={trainee.mentorChoice3 === null}
                    checked={trainee.mentorChoice3 === trainee.mentorChoice && trainee.mentorChoice3 !== null}
                    value={trainee.mentorChoice3 === null ? "" : trainee.mentorChoice3}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeMentorChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={trainee.mentorChoice3Name === null ? "n/a" : trainee.mentorChoice3Name}
                  />
                  <FormControlLabel
                    checked={
                      trainee.mentorChoice !== null &&
                      trainee.mentorChoice1 !== trainee.mentorChoice &&
                      trainee.mentorChoice2 !== trainee.mentorChoice &&
                      trainee.mentorChoice3 !== trainee.mentorChoice
                    }
                    control={<Radio onClick={() => changeMentorChoice(trainee.memberId, "")} />}
                    label={
                      <TextField
                        select
                        sx={{ minWidth: "80px" }}
                        label="Andere"
                        value={
                          trainee.mentorChoice !== null &&
                          trainee.mentorChoice1 !== trainee.mentorChoice &&
                          trainee.mentorChoice2 !== trainee.mentorChoice &&
                          trainee.mentorChoice3 !== trainee.mentorChoice
                            ? trainee.mentorChoice
                            : ""
                        }
                        onChange={(event) => changeMentorChoice(trainee.memberId, event.target.value.toString())}
                      >
                        {mentorsOfGeneration.map((mentor) => {
                          if (mentor.memberId) {
                            return (
                              <MenuItem key={mentor.memberId} value={mentor.memberId}>
                                {mentor.firstname.charAt(0) + "." + mentor.lastname}
                              </MenuItem>
                            );
                          }
                        })}
                      </TextField>
                    }
                  />
                </RadioGroup>
              </TableCell>
              <TableCell sx={styles.tableCell}>
                <RadioGroup row aria-labelledby="demo-row-radio-buttons-group-label" name="row-radio-buttons-group">
                  <FormControlLabel
                    disabled={trainee.internalProjectChoice1 === null}
                    checked={
                      trainee.internalProjectChoice1 === trainee.internalProjectChoice &&
                      trainee.internalProjectChoice1 !== null
                    }
                    value={trainee.internalProjectChoice1 === null ? "" : trainee.internalProjectChoice1}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeInternalProjectChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={
                      trainee.internalProjectChoice1ShortName === null ? "n/a" : trainee.internalProjectChoice1ShortName
                    }
                  />
                  <FormControlLabel
                    disabled={trainee.internalProjectChoice2 === null}
                    checked={
                      trainee.internalProjectChoice2 === trainee.internalProjectChoice &&
                      trainee.internalProjectChoice2 !== null
                    }
                    value={trainee.internalProjectChoice2 === null ? "" : trainee.internalProjectChoice2}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeInternalProjectChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={
                      trainee.internalProjectChoice2ShortName === null ? "n/a" : trainee.internalProjectChoice2ShortName
                    }
                  />
                  <FormControlLabel
                    disabled={trainee.internalProjectChoice3 === null}
                    checked={
                      trainee.internalProjectChoice3 === trainee.internalProjectChoice &&
                      trainee.internalProjectChoice3 !== null
                    }
                    value={trainee.internalProjectChoice3 === null ? "" : trainee.internalProjectChoice3}
                    control={
                      <Radio
                        onClick={(event) =>
                          changeInternalProjectChoice(trainee.memberId, (event.target as HTMLInputElement).value)
                        }
                      />
                    }
                    label={
                      trainee.internalProjectChoice3ShortName === null ? "n/a" : trainee.internalProjectChoice3ShortName
                    }
                  />
                  <FormControlLabel
                    checked={
                      trainee.internalProjectChoice !== null &&
                      trainee.internalProjectChoice1 !== trainee.internalProjectChoice &&
                      trainee.internalProjectChoice2 !== trainee.internalProjectChoice &&
                      trainee.internalProjectChoice3 !== trainee.internalProjectChoice
                    }
                    control={<Radio onClick={() => changeInternalProjectChoice(trainee.memberId, "")} />}
                    label={
                      <TextField
                        select
                        sx={{ minWidth: "80px" }}
                        label="Andere"
                        value={
                          trainee.internalProjectChoice !== null &&
                          trainee.internalProjectChoice1 !== trainee.internalProjectChoice &&
                          trainee.internalProjectChoice2 !== trainee.internalProjectChoice &&
                          trainee.internalProjectChoice3 !== trainee.internalProjectChoice
                            ? trainee.internalProjectChoice
                            : ""
                        }
                        onChange={(event) =>
                          changeInternalProjectChoice(trainee.memberId, event.target.value.toString())
                        }
                      >
                        {internalProjectsOfGeneration.map((project) => {
                          return (
                            <MenuItem key={project.internalProjectId} value={project.internalProjectId}>
                              {project.abbreviation}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    }
                  />
                </RadioGroup>
              </TableCell>
              <TableCell sx={{ padding: "10px 8px 10px 8px", marginRight: "16px", minWidth: "16px" }}>
                <Button
                  sx={{ padding: "10px 8px 10px 8px", marginRight: "16px", minWidth: "16px" }}
                  onClick={() => {
                    openMotivationalTexts(trainee);
                  }}
                >
                  <AttachFileSharpIcon />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TraineePreferencesTable;
